/**
 * Rent reminder email templates (German verbatim per Section 11 of the
 * Engineering Spec). One function returns subject + HTML + plain text for
 * a given stage; both the admin preview modal and the `send-rent-reminder`
 * edge function consume it so the draft a tenant SEES is byte-identical to
 * what gets sent.
 *
 * Stages (per Section 4):
 *   1 = Friendly reminder (Erinnerung)   — informal, no fees
 *   2 = 1. Mahnung                        — formal, 1 × €5 fee
 *   3 = 2. Mahnung                        — formal, 2 × €5 fees
 *   4 = Inkasso referral                  — formal legal warning, 3+ × €5 fees + inkasso
 *
 * Fees are listed in the email's itemised table; the admin sees the total.
 * Stage 4 emails carry an extra paragraph about the case being handed to
 * the collector and about additional inkasso/court costs.
 */

export type ReminderStage = 1 | 2 | 3 | 4;
/** Template language. German is canonical (per spec section 11) and remains
 *  the default; English templates exist for tenants whose `profile.language`
 *  is set to EN, and are rendered in the admin preview when the toggle is
 *  switched. */
export type ReminderLang = "de" | "en";

export interface ReminderTemplateInput {
  /** First name only — used in the friendly stage 1 salutation. */
  firstName: string;
  /** Full name (e.g. "Anna Schmidt") used in formal stages 2–4. */
  fullName: string;
  /** Room/property label, e.g. "Cozy Nest 15a · Augsburg". */
  room: string;
  /** Period the rent is owed for, e.g. "Mai 2026". */
  month: string;
  /** Original rent due date, formatted "01.05.2026". */
  dueDate: string;
  /** Net rent in euros (no fees). */
  rentEur: number;
  /**
   * How many €5 Mahngebühr lines to include in the itemised table.
   *   Stage 1 → 0
   *   Stage 2 → 1
   *   Stage 3 → 2
   *   Stage 4 → 3 (or more on inkasso re-sends)
   */
  feeCount: number;
  /**
   * Day-of-month for the recurring rent due date (1–31).
   * When provided, each fee row shows its exact escalation trigger date
   * computed from this day, together with its stage label (1. Mahnung, etc.).
   */
  rentDueDay?: number;
  /**
   * Fallback formatted fee dates (oldest first). Used only when rentDueDay is
   * not provided. Kept for backwards compat with older call sites.
   */
  feeDates?: string[];
  /** ISO date string used for the "by [date+2]" deadline in stage 3. */
  stage3DeadlineDate?: string;
  /**
   * Per-property Mahngebühr in EUR (COL-308,
   * properties.dunning_fee_per_stage_eur). Defaults to the €5 spec value so
   * all existing call sites stay valid. Resolve raw DB values through
   * resolveDunningFee before passing.
   */
  feePerStage?: number;
}

export interface RenderedReminder {
  subject: string;
  /** HTML body including a styled itemised invoice table. */
  html: string;
  /** Plain-text body — same content in a no-formatting fallback. */
  text: string;
}

const FEE_PER_STAGE_EUR = 5;

/**
 * Sanitize a per-property fee value: finite, non-negative, 2 decimals;
 * anything else falls back to the €5 default. Frontend mirror of
 * resolveDunningFee in supabase/functions/_shared/dunning.ts — keep in sync.
 */
export function resolveDunningFee(value: unknown): number {
  if (value === null || value === undefined || value === "")
    return FEE_PER_STAGE_EUR;
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return FEE_PER_STAGE_EUR;
  return Math.round(n * 100) / 100;
}

const COMPANY_FOOTER_HTML = `
  <p style="margin:24px 0 0 0;font-size:12px;color:#94a3b8;line-height:1.6;border-top:1px solid #e2e8f0;padding-top:16px;">
    Coliviq GmbH · Elvirastraße 10 · 80636 München · <a href="mailto:rechnungen@coliviq.de" style="color:#0d9488;font-weight:600;text-decoration:none;">rechnungen@coliviq.de</a>
  </p>`;

const COMPANY_FOOTER_TEXT =
  "\n\nColiviq GmbH · Elvirastraße 10 · 80636 München · rechnungen@coliviq.de";

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function formatEur(amount: number, lang: ReminderLang = "de"): string {
  // German tenants see "1.234,56 €", English tenants see "€1,234.56".
  return new Intl.NumberFormat(lang === "de" ? "de-DE" : "en-IE", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

// ───────────────────────────────────────────────────────────────────────────
// Escalation schedule — trigger day offsets from the due date
// ───────────────────────────────────────────────────────────────────────────

// Fee is added at each of these day offsets from the rent due date.
// Index 0 = 1st fee (Stage 2 / 1. Mahnung), index 1 = 2nd fee (Stage 3),
// index 2 = 3rd fee (Stage 4 Inkasso first send), index 3+ = Stage 4 repeats
// every 2 days (+2 each time).
const FEE_TRIGGER_DAYS = [5, 7, 9]; // stages 2, 3, 4

/** Stage label per fee index — used in the invoice table row. */
const FEE_STAGE_LABEL: Record<ReminderLang, (i: number) => string> = {
  de: (i) => (i === 0 ? "1. Mahnung" : i === 1 ? "2. Mahnung" : "Inkasso"),
  en: (i) => (i === 0 ? "1st Mahnung" : i === 1 ? "2nd Mahnung" : "Inkasso"),
};

/** Returns the UTC trigger day-offset for fee at index i. */
function feeTriggerDay(i: number): number {
  if (i < FEE_TRIGGER_DAYS.length) return FEE_TRIGGER_DAYS[i];
  // Stage-4 repeats: every 2 days after day 9
  return 9 + (i - 2) * 2;
}

/**
 * Compute the most recent occurrence of `rentDueDay` as a UTC midnight Date.
 * If today's day-of-month >= rentDueDay → due date is this month.
 * Otherwise → due date was last month.
 */
function computeActualDueDate(
  rentDueDay: number,
  today: Date = new Date()
): Date {
  const todayUtc = new Date(
    Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate())
  );
  const d = todayUtc.getUTCDate();
  if (d >= rentDueDay) {
    const lastDay = new Date(
      Date.UTC(todayUtc.getUTCFullYear(), todayUtc.getUTCMonth() + 1, 0)
    ).getUTCDate();
    return new Date(
      Date.UTC(
        todayUtc.getUTCFullYear(),
        todayUtc.getUTCMonth(),
        Math.min(rentDueDay, lastDay)
      )
    );
  }
  const lastDayPrev = new Date(
    Date.UTC(todayUtc.getUTCFullYear(), todayUtc.getUTCMonth(), 0)
  ).getUTCDate();
  return new Date(
    Date.UTC(
      todayUtc.getUTCFullYear(),
      todayUtc.getUTCMonth() - 1,
      Math.min(rentDueDay, lastDayPrev)
    )
  );
}

/** Format a UTC Date as "07.05.2026" (DE) or "07/05/2026" (EN). */
function formatEscalationDate(date: Date, lang: ReminderLang): string {
  return date.toLocaleDateString(lang === "de" ? "de-DE" : "en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone: "UTC",
  });
}

// ───────────────────────────────────────────────────────────────────────────
// Localised strings used by the invoice table + shell
// ───────────────────────────────────────────────────────────────────────────

const STR = {
  de: {
    htmlLang: "de",
    invoicePosition: "Position",
    invoiceAmount: "Betrag",
    invoiceRent: (month: string) => `Miete ${month}`,
    invoiceFeeRow: (n: number, date?: string) =>
      date ? `Mahngebühr ${date}` : `Mahngebühr ${n}.`,
    invoiceTotal: "Gesamtbetrag",
  },
  en: {
    htmlLang: "en",
    invoicePosition: "Item",
    invoiceAmount: "Amount",
    invoiceRent: (month: string) => `Rent · ${month}`,
    invoiceFeeRow: (n: number, date?: string) =>
      date
        ? `Reminder fee · ${date}`
        : n === 1
          ? "1st reminder fee (Mahngebühr)"
          : n === 2
            ? "2nd reminder fee (Mahngebühr)"
            : n === 3
              ? "3rd reminder fee (Mahngebühr)"
              : `${n}th reminder fee (Mahngebühr)`,
    invoiceTotal: "Total due",
  },
};

/** Builds the itemised invoice table. Always includes the rent line; adds
 * one €5 row per escalation stage. When `rentDueDay` is provided each row
 * shows its exact trigger date (due_date + N days) and a stage label
 * (1. Mahnung / 2. Mahnung / Inkasso). Falls back to legacy `feeDates` or
 * ordinal labels when `rentDueDay` is absent. */
function renderInvoiceTable(input: {
  rentEur: number;
  feeCount: number;
  month: string;
  lang: ReminderLang;
  rentDueDay?: number;
  feeDates?: string[];
  /** Per-property Mahngebühr (COL-308); defaults to the €5 spec value. */
  feePerStage?: number;
}): { html: string; text: string; totalEur: number } {
  const { rentEur, feeCount, month, lang, rentDueDay, feeDates } = input;
  const feePerStage = input.feePerStage ?? FEE_PER_STAGE_EUR;
  const s = STR[lang];
  const totalEur = rentEur + feeCount * feePerStage;

  // Build one fee row descriptor per fee. Prefer dynamic calculation from
  // rentDueDay; fall back to feeDates array; then ordinal labels.
  type FeeRow = { label: string; amountEur: number };
  const feeRows: FeeRow[] = [];

  if (rentDueDay && rentDueDay >= 1 && rentDueDay <= 31) {
    const dueDate = computeActualDueDate(rentDueDay);
    for (let i = 0; i < feeCount; i++) {
      const triggerDate = new Date(
        dueDate.getTime() + feeTriggerDay(i) * 86_400_000
      );
      const dateStr = formatEscalationDate(triggerDate, lang);
      const stageLabel = FEE_STAGE_LABEL[lang](i);
      const label =
        lang === "de"
          ? `Mahngebühr ${dateStr} (${stageLabel})`
          : `Reminder fee ${dateStr} (${stageLabel})`;
      feeRows.push({ label, amountEur: feePerStage });
    }
  } else if (feeDates && feeDates.length > 0) {
    // Legacy path: group by date, one row per unique date
    type FeeGroup = { date: string | undefined; count: number };
    const groups: FeeGroup[] = [];
    for (let i = 0; i < feeCount; i++) {
      const date = feeDates[i];
      const last = groups[groups.length - 1];
      if (last && last.date === date) {
        last.count++;
      } else {
        groups.push({ date, count: 1 });
      }
    }
    groups.forEach(({ date, count }, idx) => {
      const base = s.invoiceFeeRow(idx + 1, date);
      feeRows.push({
        label: count > 1 ? `${base} (×${count})` : base,
        amountEur: count * feePerStage,
      });
    });
  } else {
    for (let i = 0; i < feeCount; i++) {
      feeRows.push({
        label: s.invoiceFeeRow(i + 1),
        amountEur: feePerStage,
      });
    }
  }

  const rowDivider = "rgba(15, 23, 42, 0.08)";
  const feeRowsHtml = feeRows
    .map(
      ({ label, amountEur }) => `
        <tr>
          <td style="padding:10px 12px;border-bottom:1px solid ${rowDivider};font-size:14px;color:#0f172a;">
            ${escapeHtml(label)}
          </td>
          <td style="padding:10px 12px;border-bottom:1px solid ${rowDivider};font-size:14px;color:#0f172a;text-align:right;font-variant-numeric:tabular-nums;">
            ${escapeHtml(formatEur(amountEur, lang))}
          </td>
        </tr>`
    )
    .join("");

  const html = `
      <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="margin:18px 0;border-collapse:collapse;">
        <thead>
          <tr style="background:rgba(15, 23, 42, 0.04);">
            <th align="left" style="padding:12px 12px;font-size:11px;color:#94a3b8;font-weight:700;text-transform:uppercase;letter-spacing:0.08em;border-bottom:1px solid ${rowDivider};">${escapeHtml(s.invoicePosition)}</th>
            <th align="right" style="padding:12px 12px;font-size:11px;color:#94a3b8;font-weight:700;text-transform:uppercase;letter-spacing:0.08em;border-bottom:1px solid ${rowDivider};">${escapeHtml(s.invoiceAmount)}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style="padding:14px 12px;border-bottom:1px solid ${rowDivider};font-size:14px;color:#0f172a;">
              ${escapeHtml(s.invoiceRent(month))}
            </td>
            <td style="padding:14px 12px;border-bottom:1px solid ${rowDivider};font-size:14px;color:#0f172a;text-align:right;font-variant-numeric:tabular-nums;">
              ${escapeHtml(formatEur(rentEur, lang))}
            </td>
          </tr>
          ${feeRowsHtml}
          <tr>
            <td style="padding:18px 12px 8px 12px;font-size:16px;font-weight:800;color:#0f172a;">
              ${escapeHtml(s.invoiceTotal)}
            </td>
            <td style="padding:18px 12px 8px 12px;font-size:18px;font-weight:900;color:#0f172a;text-align:right;font-variant-numeric:tabular-nums;">
              ${escapeHtml(formatEur(totalEur, lang))}
            </td>
          </tr>
        </tbody>
      </table>`;

  const lines: string[] = [];
  lines.push(
    `  ${s.invoiceRent(month).padEnd(38)} ${formatEur(rentEur, lang)}`
  );
  feeRows.forEach(({ label, amountEur }) => {
    lines.push(`  ${label.padEnd(38)} ${formatEur(amountEur, lang)}`);
  });
  lines.push("  ─────────────────────────────────────────");
  lines.push(`  ${s.invoiceTotal.padEnd(38)} ${formatEur(totalEur, lang)}`);
  const text = lines.join("\n");

  return { html, text, totalEur };
}

/** Wrap a body of HTML in our standard CoLiviq email shell. */
function shell(opts: {
  headlineLabel: string;
  subject: string;
  bodyHtml: string;
  lang: ReminderLang;
}): string {
  const { headlineLabel, subject, bodyHtml, lang } = opts;
  return `<!DOCTYPE html>
<html lang="${STR[lang].htmlLang}">
<head>
  <meta charset="utf-8"/>
  <meta name="viewport" content="width=device-width,initial-scale=1"/>
  <title>${escapeHtml(subject)}</title>
</head>
<body style="margin:0;padding:0;background-color:#f1f5f9;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif;font-size:16px;line-height:1.6;color:#334155;">
  <div style="display:none;max-height:0;overflow:hidden;mso-hide:all;">${escapeHtml(subject)}</div>
  <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="background-color:#f1f5f9;">
    <tr>
      <td align="center" style="padding:32px 16px;">
        <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="max-width:640px;margin:0 auto;background-color:#ffffff;border-radius:16px;overflow:hidden;box-shadow:0 10px 40px -12px rgba(15,23,42,0.12),0 4px 16px -4px rgba(15,23,42,0.08);">
          <tr>
            <td style="background:linear-gradient(135deg,#14b8a6 0%,#0d9488 45%,#0f766e 100%);padding:24px 32px;text-align:center;">
              <span style="display:inline-block;background:rgba(255,255,255,0.22);color:#fff;font-size:11px;font-weight:600;letter-spacing:0.12em;text-transform:uppercase;padding:6px 14px;border-radius:999px;">${escapeHtml(headlineLabel)}</span>
            </td>
          </tr>
          <tr>
            <td style="padding:28px 32px 32px 32px;">
              ${bodyHtml}
              ${COMPANY_FOOTER_HTML}
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
}

// ───────────────────────────────────────────────────────────────────────────
// Stage 1 — Friendly reminder (Erinnerung)
// ───────────────────────────────────────────────────────────────────────────

function renderStage1De(input: ReminderTemplateInput): RenderedReminder {
  const { firstName, room, month, dueDate, rentEur, feePerStage } = input;
  const subject = `Erinnerung: Ausstehende Mietzahlung – ${room}`;
  const invoice = renderInvoiceTable({
    rentEur,
    feeCount: 0,
    month,
    lang: "de",
    feePerStage,
  });

  const html = shell({
    headlineLabel: "Erinnerung",
    subject,
    lang: "de",
    bodyHtml: `
      <p style="margin:0 0 16px 0;font-size:17px;color:#0f172a;">Hallo ${escapeHtml(firstName)},</p>
      <p style="margin:0 0 14px 0;font-size:15px;color:#475569;line-height:1.65;">
        wir hoffen, es geht Ihnen gut. Uns ist aufgefallen, dass die Mietzahlung für <strong>${escapeHtml(month)}</strong> für Ihr Zimmer <strong>${escapeHtml(room)}</strong> noch nicht bei uns eingegangen ist. Der Fälligkeitstermin war der <strong>${escapeHtml(dueDate)}</strong>.
      </p>
      <p style="margin:0 0 14px 0;font-size:15px;color:#475569;line-height:1.65;">
        Bitte überprüfen Sie Ihre Bankdaten und veranlassen Sie die Überweisung so bald wie möglich. Sollte die Zahlung bereits erfolgt sein, bitten wir Sie, diese Nachricht zu ignorieren.
      </p>
      ${invoice.html}
      <p style="margin:18px 0 14px 0;font-size:15px;color:#475569;line-height:1.65;">
        Bei Fragen stehen wir jederzeit zur Verfügung.
      </p>
      <p style="margin:0;font-size:15px;color:#0f172a;">Herzliche Grüße,<br/>Das coliviq Team</p>
    `,
  });

  const text =
    `Hallo ${firstName},\n\n` +
    `wir hoffen, es geht Ihnen gut. Uns ist aufgefallen, dass die Mietzahlung für ${month} für Ihr Zimmer ${room} noch nicht bei uns eingegangen ist. Der Fälligkeitstermin war der ${dueDate}.\n\n` +
    `Bitte überprüfen Sie Ihre Bankdaten und veranlassen Sie die Überweisung so bald wie möglich. Sollte die Zahlung bereits erfolgt sein, bitten wir Sie, diese Nachricht zu ignorieren.\n\n` +
    `${invoice.text}\n\n` +
    `Bei Fragen stehen wir jederzeit zur Verfügung.\n\n` +
    `Herzliche Grüße,\nDas coliviq Team` +
    COMPANY_FOOTER_TEXT;

  return { subject, html, text };
}

// ───────────────────────────────────────────────────────────────────────────
// Stage 2 — 1. Mahnung
// ───────────────────────────────────────────────────────────────────────────

function renderStage2De(input: ReminderTemplateInput): RenderedReminder {
  const {
    fullName,
    room,
    month,
    dueDate,
    rentEur,
    rentDueDay,
    feeDates,
    feePerStage,
  } = input;
  const subject = `1. Mahnung: Ausstehende Mietzahlung – ${room}`;
  const invoice = renderInvoiceTable({
    rentEur,
    feeCount: 1,
    month,
    lang: "de",
    rentDueDay,
    feeDates,
    feePerStage,
  });

  const html = shell({
    headlineLabel: "1. Mahnung",
    subject,
    lang: "de",
    bodyHtml: `
      <p style="margin:0 0 16px 0;font-size:17px;color:#0f172a;">Sehr geehrte/r ${escapeHtml(fullName)},</p>
      <p style="margin:0 0 14px 0;font-size:15px;color:#475569;line-height:1.65;">
        trotz unserer vorherigen Zahlungserinnerung haben wir bis heute keinen Zahlungseingang für die Miete <strong>${escapeHtml(month)}</strong> für Ihr Zimmer <strong>${escapeHtml(room)}</strong> feststellen können. Der ursprüngliche Fälligkeitstermin war der <strong>${escapeHtml(dueDate)}</strong>.
      </p>
      <p style="margin:0 0 14px 0;font-size:15px;color:#475569;line-height:1.65;">
        Wir fordern Sie hiermit auf, den ausstehenden Betrag inklusive der angefallenen Mahngebühren unverzüglich zu begleichen.
      </p>
      ${invoice.html}
      <p style="margin:18px 0 14px 0;font-size:15px;color:#475569;line-height:1.65;">
        Bitte beachten Sie, dass bei weiterer Nichtzahlung weitere rechtliche Schritte eingeleitet werden. Wir bitten Sie dringend, Ihre Zahlung sofort vorzunehmen.
      </p>
      <p style="margin:0;font-size:15px;color:#0f172a;">Mit freundlichen Grüßen,<br/>Das coliviq Team</p>
    `,
  });

  const text =
    `Sehr geehrte/r ${fullName},\n\n` +
    `trotz unserer vorherigen Zahlungserinnerung haben wir bis heute keinen Zahlungseingang für die Miete ${month} für Ihr Zimmer ${room} feststellen können. Der ursprüngliche Fälligkeitstermin war der ${dueDate}.\n\n` +
    `Wir fordern Sie hiermit auf, den ausstehenden Betrag inklusive der angefallenen Mahngebühren unverzüglich zu begleichen.\n\n` +
    `${invoice.text}\n\n` +
    `Bitte beachten Sie, dass bei weiterer Nichtzahlung weitere rechtliche Schritte eingeleitet werden. Wir bitten Sie dringend, Ihre Zahlung sofort vorzunehmen.\n\n` +
    `Mit freundlichen Grüßen,\nDas coliviq Team` +
    COMPANY_FOOTER_TEXT;

  return { subject, html, text };
}

// ───────────────────────────────────────────────────────────────────────────
// Stage 3 — 2. Mahnung
// ───────────────────────────────────────────────────────────────────────────

function renderStage3De(input: ReminderTemplateInput): RenderedReminder {
  const {
    fullName,
    room,
    month,
    rentEur,
    stage3DeadlineDate,
    rentDueDay,
    feeDates,
    feePerStage,
  } = input;
  const subject = `2. Mahnung: Ausstehende Mietzahlung – ${room}`;
  const invoice = renderInvoiceTable({
    rentEur,
    feeCount: 2,
    month,
    lang: "de",
    rentDueDay,
    feeDates,
    feePerStage,
  });
  const deadline = stage3DeadlineDate || "[Datum]";

  const html = shell({
    headlineLabel: "2. Mahnung",
    subject,
    lang: "de",
    bodyHtml: `
      <p style="margin:0 0 16px 0;font-size:17px;color:#0f172a;">Sehr geehrte/r ${escapeHtml(fullName)},</p>
      <p style="margin:0 0 14px 0;font-size:15px;color:#475569;line-height:1.65;">
        auch nach unserer ersten Mahnung ist kein Zahlungseingang für die Miete <strong>${escapeHtml(month)}</strong> bei uns eingegangen. Wir weisen Sie erneut und letztmalig auf den ausstehenden Betrag hin.
      </p>
      ${invoice.html}
      <p style="margin:18px 0 14px 0;font-size:15px;color:#475569;line-height:1.65;">
        Sollte der Betrag nicht bis <strong>${escapeHtml(deadline)}</strong> auf unserem Konto eingehen, sehen wir uns gezwungen, Ihren Fall an unser Inkassounternehmen zu übergeben. Damit verbunden sind erhebliche zusätzliche Kosten, die Sie zu tragen haben.
      </p>
      <p style="margin:0;font-size:15px;color:#0f172a;">Mit freundlichen Grüßen,<br/>Das coliviq Team</p>
    `,
  });

  const text =
    `Sehr geehrte/r ${fullName},\n\n` +
    `auch nach unserer ersten Mahnung ist kein Zahlungseingang für die Miete ${month} bei uns eingegangen. Wir weisen Sie erneut und letztmalig auf den ausstehenden Betrag hin.\n\n` +
    `${invoice.text}\n\n` +
    `Sollte der Betrag nicht bis ${deadline} auf unserem Konto eingehen, sehen wir uns gezwungen, Ihren Fall an unser Inkassounternehmen zu übergeben. Damit verbunden sind erhebliche zusätzliche Kosten, die Sie zu tragen haben.\n\n` +
    `Mit freundlichen Grüßen,\nDas coliviq Team` +
    COMPANY_FOOTER_TEXT;

  return { subject, html, text };
}

// ───────────────────────────────────────────────────────────────────────────
// Stage 4 — Inkasso referral
// ───────────────────────────────────────────────────────────────────────────

function renderStage4De(input: ReminderTemplateInput): RenderedReminder {
  const {
    fullName,
    room,
    month,
    rentEur,
    feeCount,
    rentDueDay,
    feeDates,
    feePerStage,
  } = input;
  const subject = `Inkasso: Übergabe Ihres Falls – ${room}`;
  const invoice = renderInvoiceTable({
    rentEur,
    feeCount: Math.max(3, feeCount),
    month,
    lang: "de",
    rentDueDay,
    feeDates,
    feePerStage,
  });

  const html = shell({
    headlineLabel: "Inkasso-Benachrichtigung",
    subject,
    lang: "de",
    bodyHtml: `
      <p style="margin:0 0 16px 0;font-size:17px;color:#0f172a;">Sehr geehrte/r ${escapeHtml(fullName)},</p>
      <p style="margin:0 0 14px 0;font-size:15px;color:#475569;line-height:1.65;">
        da unsere vorangegangenen Mahnungen unbeantwortet blieben, haben wir uns gezwungen gesehen, Ihren Fall an unser Inkassounternehmen zu übergeben.
      </p>
      <p style="margin:0 0 14px 0;font-size:15px;color:#475569;line-height:1.65;">
        Bitte beachten Sie, dass ab diesem Zeitpunkt zusätzliche Inkasso- und Gerichtskosten anfallen, die Sie ebenfalls zu tragen haben. Diese Kosten übersteigen in der Regel die aufgelaufenen Mahngebühren erheblich.
      </p>
      ${invoice.html}
      <p style="margin:18px 0 14px 0;font-size:15px;color:#475569;line-height:1.65;">
        Zahlungen sind nun ausschließlich direkt an das Inkassounternehmen zu leisten. Sie werden separat kontaktiert. Die Verbindlichkeiten gegenüber coliviq bleiben bis zur vollständigen Zahlung bestehen.
      </p>
      <p style="margin:0;font-size:15px;color:#0f172a;">Mit freundlichen Grüßen,<br/>Das coliviq Team</p>
    `,
  });

  const text =
    `Sehr geehrte/r ${fullName},\n\n` +
    `da unsere vorangegangenen Mahnungen unbeantwortet blieben, haben wir uns gezwungen gesehen, Ihren Fall an unser Inkassounternehmen zu übergeben.\n\n` +
    `Bitte beachten Sie, dass ab diesem Zeitpunkt zusätzliche Inkasso- und Gerichtskosten anfallen, die Sie ebenfalls zu tragen haben. Diese Kosten übersteigen in der Regel die aufgelaufenen Mahngebühren erheblich.\n\n` +
    `${invoice.text}\n\n` +
    `Zahlungen sind nun ausschließlich direkt an das Inkassounternehmen zu leisten. Sie werden separat kontaktiert. Die Verbindlichkeiten gegenüber coliviq bleiben bis zur vollständigen Zahlung bestehen.\n\n` +
    `Mit freundlichen Grüßen,\nDas coliviq Team` +
    COMPANY_FOOTER_TEXT;

  return { subject, html, text };
}

// ───────────────────────────────────────────────────────────────────────────
// English templates — same structure / same fees, English copy. Subject
// lines mirror the German ones for translation parity.
// ───────────────────────────────────────────────────────────────────────────

function renderStage1En(input: ReminderTemplateInput): RenderedReminder {
  const { firstName, room, month, dueDate, rentEur, feePerStage } = input;
  const subject = `Friendly reminder: Outstanding rent payment – ${room}`;
  const invoice = renderInvoiceTable({
    rentEur,
    feeCount: 0,
    month,
    lang: "en",
    feePerStage,
  });

  const html = shell({
    headlineLabel: "Friendly reminder",
    subject,
    lang: "en",
    bodyHtml: `
      <p style="margin:0 0 16px 0;font-size:17px;color:#0f172a;">Hi ${escapeHtml(firstName)},</p>
      <p style="margin:0 0 14px 0;font-size:15px;color:#475569;line-height:1.65;">
        We hope you're doing well. We noticed that the rent payment for <strong>${escapeHtml(month)}</strong> for your room <strong>${escapeHtml(room)}</strong> hasn't reached us yet. The original due date was <strong>${escapeHtml(dueDate)}</strong>.
      </p>
      <p style="margin:0 0 14px 0;font-size:15px;color:#475569;line-height:1.65;">
        Please double-check your bank details and arrange the transfer as soon as possible. If you've already paid, please disregard this message.
      </p>
      ${invoice.html}
      <p style="margin:18px 0 14px 0;font-size:15px;color:#475569;line-height:1.65;">
        We're here if you have any questions.
      </p>
      <p style="margin:0;font-size:15px;color:#0f172a;">Kind regards,<br/>The coliviq Team</p>
    `,
  });

  const text =
    `Hi ${firstName},\n\n` +
    `We hope you're doing well. We noticed that the rent payment for ${month} for your room ${room} hasn't reached us yet. The original due date was ${dueDate}.\n\n` +
    `Please double-check your bank details and arrange the transfer as soon as possible. If you've already paid, please disregard this message.\n\n` +
    `${invoice.text}\n\n` +
    `We're here if you have any questions.\n\n` +
    `Kind regards,\nThe coliviq Team` +
    COMPANY_FOOTER_TEXT;

  return { subject, html, text };
}

function renderStage2En(input: ReminderTemplateInput): RenderedReminder {
  const {
    fullName,
    room,
    month,
    dueDate,
    rentEur,
    rentDueDay,
    feeDates,
    feePerStage,
  } = input;
  const subject = `1st Notice: Outstanding rent payment – ${room}`;
  const invoice = renderInvoiceTable({
    rentEur,
    feeCount: 1,
    month,
    lang: "en",
    rentDueDay,
    feeDates,
    feePerStage,
  });

  const html = shell({
    headlineLabel: "1st Notice",
    subject,
    lang: "en",
    bodyHtml: `
      <p style="margin:0 0 16px 0;font-size:17px;color:#0f172a;">Dear ${escapeHtml(fullName)},</p>
      <p style="margin:0 0 14px 0;font-size:15px;color:#475569;line-height:1.65;">
        Despite our previous reminder, we have not yet received your rent payment for <strong>${escapeHtml(month)}</strong> for your room <strong>${escapeHtml(room)}</strong>. The original due date was <strong>${escapeHtml(dueDate)}</strong>.
      </p>
      <p style="margin:0 0 14px 0;font-size:15px;color:#475569;line-height:1.65;">
        We kindly ask you to settle the outstanding balance, including the accrued reminder fees, without further delay.
      </p>
      ${invoice.html}
      <p style="margin:18px 0 14px 0;font-size:15px;color:#475569;line-height:1.65;">
        Please note that further legal steps will be taken if payment is not received. We urge you to make your payment immediately.
      </p>
      <p style="margin:0;font-size:15px;color:#0f172a;">Sincerely,<br/>The coliviq Team</p>
    `,
  });

  const text =
    `Dear ${fullName},\n\n` +
    `Despite our previous reminder, we have not yet received your rent payment for ${month} for your room ${room}. The original due date was ${dueDate}.\n\n` +
    `We kindly ask you to settle the outstanding balance, including the accrued reminder fees, without further delay.\n\n` +
    `${invoice.text}\n\n` +
    `Please note that further legal steps will be taken if payment is not received. We urge you to make your payment immediately.\n\n` +
    `Sincerely,\nThe coliviq Team` +
    COMPANY_FOOTER_TEXT;

  return { subject, html, text };
}

function renderStage3En(input: ReminderTemplateInput): RenderedReminder {
  const {
    fullName,
    room,
    month,
    rentEur,
    stage3DeadlineDate,
    rentDueDay,
    feeDates,
    feePerStage,
  } = input;
  const subject = `2nd Notice: Outstanding rent payment – ${room}`;
  const invoice = renderInvoiceTable({
    rentEur,
    feeCount: 2,
    month,
    lang: "en",
    rentDueDay,
    feeDates,
    feePerStage,
  });
  const deadline = stage3DeadlineDate || "[date]";

  const html = shell({
    headlineLabel: "2nd Notice",
    subject,
    lang: "en",
    bodyHtml: `
      <p style="margin:0 0 16px 0;font-size:17px;color:#0f172a;">Dear ${escapeHtml(fullName)},</p>
      <p style="margin:0 0 14px 0;font-size:15px;color:#475569;line-height:1.65;">
        Despite our 1st Notice, we have still not received your rent payment for <strong>${escapeHtml(month)}</strong>. This is our second and final notice before the matter is escalated.
      </p>
      ${invoice.html}
      <p style="margin:18px 0 14px 0;font-size:15px;color:#475569;line-height:1.65;">
        If the outstanding amount is not received in our account by <strong>${escapeHtml(deadline)}</strong>, we will be forced to forward your case to our debt collection agency. Substantial additional costs will then apply at your expense.
      </p>
      <p style="margin:0;font-size:15px;color:#0f172a;">Sincerely,<br/>The coliviq Team</p>
    `,
  });

  const text =
    `Dear ${fullName},\n\n` +
    `Despite our 1st Notice, we have still not received your rent payment for ${month}. This is our second and final notice before the matter is escalated.\n\n` +
    `${invoice.text}\n\n` +
    `If the outstanding amount is not received in our account by ${deadline}, we will be forced to forward your case to our debt collection agency. Substantial additional costs will then apply at your expense.\n\n` +
    `Sincerely,\nThe coliviq Team` +
    COMPANY_FOOTER_TEXT;

  return { subject, html, text };
}

function renderStage4En(input: ReminderTemplateInput): RenderedReminder {
  const {
    fullName,
    room,
    month,
    rentEur,
    feeCount,
    rentDueDay,
    feeDates,
    feePerStage,
  } = input;
  const subject = `Inkasso: Case referred – ${room}`;
  const invoice = renderInvoiceTable({
    rentEur,
    feeCount: Math.max(3, feeCount),
    month,
    lang: "en",
    rentDueDay,
    feeDates,
    feePerStage,
  });

  const html = shell({
    headlineLabel: "Inkasso notification",
    subject,
    lang: "en",
    bodyHtml: `
      <p style="margin:0 0 16px 0;font-size:17px;color:#0f172a;">Dear ${escapeHtml(fullName)},</p>
      <p style="margin:0 0 14px 0;font-size:15px;color:#475569;line-height:1.65;">
        As our previous reminders have remained unanswered, we have been forced to refer your case to our debt collection agency.
      </p>
      <p style="margin:0 0 14px 0;font-size:15px;color:#475569;line-height:1.65;">
        Please note that additional inkasso (debt collection) and court costs will apply from this point. These costs typically far exceed the accrued reminder fees.
      </p>
      ${invoice.html}
      <p style="margin:18px 0 14px 0;font-size:15px;color:#475569;line-height:1.65;">
        Payments must now be made directly to the debt collection agency. You will be contacted separately. Your liability towards coliviq remains until full payment is received.
      </p>
      <p style="margin:0;font-size:15px;color:#0f172a;">Sincerely,<br/>The coliviq Team</p>
    `,
  });

  const text =
    `Dear ${fullName},\n\n` +
    `As our previous reminders have remained unanswered, we have been forced to refer your case to our debt collection agency.\n\n` +
    `Please note that additional inkasso (debt collection) and court costs will apply from this point. These costs typically far exceed the accrued reminder fees.\n\n` +
    `${invoice.text}\n\n` +
    `Payments must now be made directly to the debt collection agency. You will be contacted separately. Your liability towards coliviq remains until full payment is received.\n\n` +
    `Sincerely,\nThe coliviq Team` +
    COMPANY_FOOTER_TEXT;

  return { subject, html, text };
}

// ───────────────────────────────────────────────────────────────────────────
// Public surface
// ───────────────────────────────────────────────────────────────────────────

export function renderReminderEmail(
  stage: ReminderStage,
  input: ReminderTemplateInput,
  lang: ReminderLang = "de"
): RenderedReminder {
  if (lang === "en") {
    switch (stage) {
      case 1:
        return renderStage1En(input);
      case 2:
        return renderStage2En(input);
      case 3:
        return renderStage3En(input);
      case 4:
        return renderStage4En(input);
    }
  }
  switch (stage) {
    case 1:
      return renderStage1De(input);
    case 2:
      return renderStage2De(input);
    case 3:
      return renderStage3De(input);
    case 4:
      return renderStage4De(input);
  }
}

/**
 * Compute days overdue from a "day of month" rent due day. The most recent
 * occurrence of that day-of-month is the period start; we count whole days
 * since then. Handles month rollover (Feb 28 → 30 if rent_due_date = 30).
 */
export function computeDaysOverdue(
  rentDueDay: number,
  today: Date = new Date()
): number {
  if (!Number.isFinite(rentDueDay) || rentDueDay < 1 || rentDueDay > 31)
    return 0;
  const todayUtc = new Date(
    Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate())
  );
  const d = todayUtc.getUTCDate();
  if (d >= rentDueDay) {
    return d - rentDueDay;
  }
  // The most recent rent_due_date was last month — count days since.
  const lastDayPrev = new Date(
    Date.UTC(todayUtc.getUTCFullYear(), todayUtc.getUTCMonth(), 0)
  ).getUTCDate();
  const dueInPrev = Math.min(rentDueDay, lastDayPrev);
  return lastDayPrev - dueInPrev + d;
}

/**
 * Stage table from Section 4. 0 means "not yet at any reminder threshold".
 */
export function computeCurrentStage(daysOverdue: number): 0 | ReminderStage {
  if (daysOverdue >= 9) return 4;
  if (daysOverdue >= 7) return 3;
  if (daysOverdue >= 5) return 2;
  if (daysOverdue >= 3) return 1;
  return 0;
}

/** Maps reminder_stage int → payment_status enum value. */
export function paymentStatusForStage(
  stage: 0 | ReminderStage
): "pending" | "overdue_1" | "overdue_2" | "overdue_3" | "inkasso" {
  if (stage === 0) return "pending";
  if (stage === 1) return "overdue_1";
  if (stage === 2) return "overdue_2";
  if (stage === 3) return "overdue_3";
  return "inkasso";
}

// ───────────────────────────────────────────────────────────────────────────
// Plain-language labels for the admin reminders UI. The internal dunning
// vocabulary (Stage N / Mahnung / Inkasso) is legally meaningful and stays in
// the DB + tenant emails, but on the dashboard it's shown in plain language a
// first-time viewer can understand, with the internal term kept on hover /
// in the tenant detail panel via reminderInternalLabel.
// ───────────────────────────────────────────────────────────────────────────

export type ReminderTone = "paid" | "late" | "final" | "off" | "pending";

/**
 * The plain-language status shown on a reminders row. `label` is the pill text,
 * `sub` is an optional muted sub-line beneath it.
 */
export function reminderStatusLabel(row: {
  reminders_enabled?: boolean;
  paid: boolean;
  days_overdue: number;
  stage: 0 | ReminderStage;
}): { label: string; sub: string | null; tone: ReminderTone } {
  if (row.reminders_enabled === false)
    return { label: "Reminders off", sub: null, tone: "off" };
  if (row.paid) return { label: "On time", sub: null, tone: "paid" };
  const d = Math.max(0, row.days_overdue);
  const late = `${d} ${d === 1 ? "day" : "days"} late`;
  if (row.stage >= 4)
    return { label: "Final notice", sub: late, tone: "final" };
  if (row.stage >= 1) {
    const ord = row.stage === 1 ? "1st" : row.stage === 2 ? "2nd" : "3rd";
    return { label: late, sub: `${ord} reminder`, tone: "late" };
  }
  if (d > 0) return { label: late, sub: "grace period", tone: "late" };
  return { label: "Pending", sub: null, tone: "pending" };
}

/**
 * The internal dunning label (Stage / Mahnung / Inkasso) — surfaced only on
 * hover and in the tenant detail panel so staff can still read the exact
 * legal step behind the plain-language status.
 */
export function reminderInternalLabel(stage: 0 | ReminderStage): string {
  if (stage === 1) return "Stage 1 · Friendly reminder";
  if (stage === 2) return "Stage 2 · 1. Mahnung";
  if (stage === 3) return "Stage 3 · 2. Mahnung";
  if (stage >= 4) return "Stage 4 · Inkasso";
  return "No reminder stage yet";
}

/** Verb-first action label for the send button, adapting to the step. */
export function reminderActionLabel(stage: 0 | ReminderStage): string {
  return stage >= 4 ? "Send final notice" : "Send reminder";
}

export const REMINDER_FEE_PER_STAGE_EUR = FEE_PER_STAGE_EUR;

/**
 * Returns the most recent due date for the given rent_due_date day-of-month.
 * If today >= dueDay, the due date is this month; otherwise it's last month.
 */
export function currentPeriodDueDate(
  rentDueDay: number,
  today: Date = new Date()
): Date {
  const d = today.getUTCDate();
  if (d >= rentDueDay) {
    return new Date(
      Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), rentDueDay)
    );
  }
  const prevMonthLastDay = new Date(
    Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 0)
  ).getUTCDate();
  return new Date(
    Date.UTC(
      today.getUTCFullYear(),
      today.getUTCMonth() - 1,
      Math.min(rentDueDay, prevMonthLastDay)
    )
  );
}

/**
 * The stage a manual "Send reminder" (tenant profile) must request from the
 * send-rent-reminder edge function. The function only accepts the stage it
 * itself computes from rent_due_date (anti-skip-ahead), so the caller must
 * derive the stage the same way — NOT reminder_stage + 1, which 400s with
 * "Stage mismatch" whenever the tenant is more than one threshold overdue.
 * 0 means "not sendable": paid for the current period, no due day on file,
 * or still inside the 3-day grace period.
 */
export function manualReminderStage(
  booking: {
    rent_due_date?: number | null;
    invoice_due_date?: string | null;
    last_payment_received_at?: string | null;
    rent_paid_through?: string | null;
  },
  today: Date = new Date()
): 0 | ReminderStage {
  if (isPaidCurrentPeriod(booking, today)) return 0;
  if (booking.invoice_due_date) {
    const due = Date.parse(booking.invoice_due_date);
    if (!Number.isFinite(due)) return 0;
    const todayUtc = Date.UTC(
      today.getUTCFullYear(),
      today.getUTCMonth(),
      today.getUTCDate()
    );
    return computeCurrentStage(
      Math.max(0, Math.floor((todayUtc - due) / 86_400_000))
    );
  }
  const rentDueDay = booking.rent_due_date ?? null;
  if (rentDueDay === null) return 0;
  return computeCurrentStage(computeDaysOverdue(rentDueDay, today));
}

/**
 * Whether a booking's rent is settled for the CURRENT billing period — the one
 * predicate every overdue/reminder surface must agree on (AdminReminders,
 * RemindersTab, PropertyCard, OwnerPropertyCard, PropertyListRow,
 * PropertyDetail). It must also agree with the tenant "Rent payments" ledger,
 * which keys coverage off each payment's due_date (the month it pays FOR).
 *
 * A booking counts as paid for the current period when ANY of:
 *   1. rent_paid_through (the latest paid MONTH from the ledger, maintained by
 *      the sync_booking_last_rent_paid trigger) reaches the current period's
 *      month — this is the due-date-based signal the ledger uses and fixes early
 *      payments (rent paid before the due day still covers the period), else
 *   2. the last received payment lands on/after the current period's due date —
 *      the receipt-time fallback the manual "Mark paid" button relies on, since
 *      it stamps last_payment_received_at but writes no ledger row (so
 *      rent_paid_through stays behind).
 *
 * `manually_marked_paid` deliberately does NOT count: it is the booking-level
 * onboarding override ("first rent + deposit settled outside Stripe", see
 * TenantProfileDialog / AdminAnalytics), set once at move-in and never reset —
 * so treating it as "current period paid" hid genuinely overdue tenants from
 * every reminder surface forever (Gülsüm, Aug 2026).
 *
 * A booking with no rent_due_date has no computable period, so a payment cannot
 * be proven to cover it → treated as not paid.
 */
export function isPaidCurrentPeriod(
  booking: {
    last_payment_received_at?: string | null;
    rent_due_date?: number | null;
    rent_paid_through?: string | null;
  },
  today: Date = new Date()
): boolean {
  const rentDueDay = booking.rent_due_date ?? null;
  if (rentDueDay === null) return false;

  // (2) Due-date ledger coverage: rent_paid_through is a first-of-month date, so
  // compare against the first-of-month of the current period.
  // rent_paid_through is the latest CONTIGUOUSLY paid-through month (as of
  // 20260822170000_fix_rent_paid_through_coverage_gaps.sql): the trigger stops
  // it at the earliest genuinely outstanding row, so an out-of-order/prepaid
  // later month can no longer mask an earlier unpaid one.
  const periodDue = currentPeriodDueDate(rentDueDay, today);
  const periodMonthMs = Date.UTC(
    periodDue.getUTCFullYear(),
    periodDue.getUTCMonth(),
    1
  );
  const paidThroughMs = booking.rent_paid_through
    ? Date.parse(booking.rent_paid_through)
    : null;
  if (paidThroughMs !== null && paidThroughMs >= periodMonthMs) return true;

  // (3) Receipt-time fallback for the manual "Mark paid" path.
  const lastPaidMs = booking.last_payment_received_at
    ? Date.parse(booking.last_payment_received_at)
    : null;
  if (lastPaidMs === null) return false;
  return lastPaidMs >= periodDue.getTime();
}

type CurrentRentBooking = Parameters<typeof isPaidCurrentPeriod>[0] & {
  /** Resolved per unit (apartment, else property, else on) by the caller. */
  reminders_enabled?: boolean | null;
};

/**
 * Days the current period's rent is overdue, as a Reminders row counts them:
 * 0 when reminders are off for the unit (neutralised, never escalated) or the
 * current period is already paid.
 */
export function rentDaysOverdue(booking: CurrentRentBooking): number {
  if (booking.reminders_enabled === false || isPaidCurrentPeriod(booking))
    return 0;
  return booking.rent_due_date ? computeDaysOverdue(booking.rent_due_date) : 0;
}

/**
 * The dunning stage a booking's current rent is at (0 = not late). One rule
 * for the Reminders tab's "Overdue" test (`payment_status !== "paid"` and a
 * stage above 0) and colivIQ Brain's late-rent roll-up (COL-527).
 */
export function lateRentStage(
  booking: CurrentRentBooking & { payment_status?: string | null }
): number {
  if (booking.payment_status === "paid") return 0;
  return computeCurrentStage(rentDaysOverdue(booking));
}

// ───────────────────────────────────────────────────────────────────────────
// Reminder sent/next-scheduled display lines (COL-381) — for the admin
// reminders UI only. Display/estimate only: does NOT change when the
// automated tick actually sends anything (see decideDunningSend in
// supabase/functions/_shared/dunning.ts, which stays the source of truth).
// ───────────────────────────────────────────────────────────────────────────

/** dunning-tick runs daily at this UTC hour — keep in sync with
 * supabase/migrations/RUN_ME_schedule_dunning_tick.sql ('0 7 * * *'). */
const DUNNING_TICK_UTC_HOUR = 7;

/** Days-overdue threshold to reach stage N (index 0 = stage 1). Mirrors
 * computeCurrentStage above. */
const STAGE_THRESHOLD_DAYS = [3, 5, 7, 9];

/** How often a stage-4 (Inkasso) booking is re-sent by the tick. Frontend
 * mirror of STAGE4_RESEND_INTERVAL_DAYS in
 * supabase/functions/_shared/dunning.ts — keep in sync. */
const STAGE4_RESEND_INTERVAL_DAYS = 2;

/** The earliest daily-07:00-UTC tick run at or after `thresholdMs`. */
function nextDailyTickAtOrAfter(thresholdMs: number): Date {
  const threshold = new Date(thresholdMs);
  const candidate = new Date(
    Date.UTC(
      threshold.getUTCFullYear(),
      threshold.getUTCMonth(),
      threshold.getUTCDate(),
      DUNNING_TICK_UTC_HOUR,
      0,
      0
    )
  );
  return candidate.getTime() >= thresholdMs
    ? candidate
    : new Date(candidate.getTime() + 86_400_000);
}

export interface NextReminderStep {
  stage: ReminderStage;
  /** When this stage is/was scheduled to go out — the daily 07:00 UTC tick at
   * or after the stage's own days-overdue threshold. A fixed point on the
   * calendar, NOT "the next run from now": for a stage whose threshold has
   * already passed unsent, this stays the day it was due. */
  at: Date;
  /** True when `at` is in the past — the run that should have sent this came
   * and went without sending. */
  missed: boolean;
}

/**
 * Estimates the next automated dunning send for display purposes: which
 * stage it will be, and roughly when (mirrors decideDunningSend's one-step
 * escalation ladder and the stage-4 resend cooldown). Returns null when
 * there's nothing left to schedule (reminders off, paid, or no due day set).
 */
export function nextReminderStep(input: {
  rentDueDay: number | null;
  remindersEnabled: boolean;
  paidCurrentPeriod: boolean;
  /** Freshly computed from days overdue (computeCurrentStage). */
  currentStage: 0 | ReminderStage;
  /** bookings.reminder_stage — the last stage actually sent. */
  lastSentStage: 0 | ReminderStage;
  reminderSentAt: string | null;
  now?: Date;
}): NextReminderStep | null {
  if (!input.remindersEnabled || input.paidCurrentPeriod) return null;
  const rentDueDay = input.rentDueDay;
  if (!rentDueDay || rentDueDay < 1 || rentDueDay > 31) return null;

  const now = input.now ?? new Date();
  const lastSentStage = Math.min(
    4,
    Math.max(0, Math.trunc(input.lastSentStage || 0))
  ) as 0 | ReminderStage;
  const currentStage = input.currentStage;

  // Already at Inkasso and caught up: governed by the resend cooldown, which
  // is anchored to the last send rather than to a days-overdue threshold.
  if (currentStage >= 4 && lastSentStage >= 4) {
    const sentMs = input.reminderSentAt
      ? Date.parse(input.reminderSentAt)
      : NaN;
    const at = Number.isFinite(sentMs)
      ? nextDailyTickAtOrAfter(
          sentMs + STAGE4_RESEND_INTERVAL_DAYS * 86_400_000
        )
      : nextDailyTickAtOrAfter(now.getTime());
    return { stage: 4, at, missed: at.getTime() < now.getTime() };
  }

  // The next rung on the ladder, and the day it is due: the 07:00 tick at or
  // after that stage's own days-overdue threshold (day 3/5/7/9 after the due
  // date). Anchoring to the THRESHOLD rather than to `now` is the point.
  //
  // This used to read `nextDailyTickAtOrAfter(now)` whenever the computed
  // stage had outrun what was sent — "whenever the next run happens to be".
  // So a stage-1 reminder due at 4 Sept 07:00 that never went out was
  // relabelled "Next: 5 Sept" the moment that morning's run passed, then
  // 6 Sept, then 7 Sept: a date that slid forward one day at a time and never
  // arrived, hiding the missed send behind a fresh promise every morning. The
  // scheduled day is a fact about the rent period, not about when someone
  // opens the page, so it stays 4 Sept — and `missed` says it went unsent.
  const nextStage = Math.min(4, lastSentStage + 1) as ReminderStage;
  const thresholdDays = STAGE_THRESHOLD_DAYS[nextStage - 1];
  const dueDate = computeActualDueDate(rentDueDay, now);
  const at = nextDailyTickAtOrAfter(
    dueDate.getTime() + thresholdDays * 86_400_000
  );
  return { stage: nextStage, at, missed: at.getTime() < now.getTime() };
}

/** "3 Aug, 07:00 UTC" — used for reminder sent/next-scheduled timestamps.
 * Explicit UTC keeps it deterministic and matches the dunning day-math above. */
export function formatReminderDateTime(value: string | Date): string {
  const d = typeof value === "string" ? new Date(value) : value;
  const datePart = d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  });
  const timePart = d.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "UTC",
  });
  return `${datePart}, ${timePart} UTC`;
}

/** The stage's plain name, no tense: "1st reminder", "Final notice". */
function reminderStagePhrase(stage: ReminderStage): string {
  if (stage >= 4) return "Final notice";
  const ord = stage === 1 ? "1st" : stage === 2 ? "2nd" : "3rd";
  return `${ord} reminder`;
}

function reminderSentPhrase(stage: ReminderStage): string {
  return `${reminderStagePhrase(stage)} sent`;
}

function reminderNextPhrase(stage: ReminderStage): string {
  return `Next: ${reminderStagePhrase(stage)}`;
}

export interface ReminderScheduleLines {
  /** e.g. "2nd reminder sent · 3 Aug, 14:32 UTC" — null when nothing sent yet. */
  sentLine: string | null;
  /** The next send and its scheduled day, e.g.
   * "Next: 3rd reminder · 5 Aug, 07:00 UTC". When that day has already passed
   * unsent it names the day it was due instead of sliding forward:
   * "1st reminder due 4 Sept, 07:00 UTC · not sent yet". Null when there is
   * nothing left to schedule. */
  nextLine: string | null;
  /** True when `nextLine` describes a send that was scheduled and did not
   * happen. The admin surfaces highlight it — a silent automated failure is
   * the one thing a muted status line must not look like. */
  nextMissed: boolean;
}

/**
 * The two extra status lines for the reminders UI (COL-381): when the last
 * reminder actually sent went out, and when the next one is estimated to go
 * out. Kept separate from reminderStatusLabel (used by AdminReminders.tsx
 * too) so this addition doesn't change that surface.
 */
export function reminderScheduleLines(input: {
  reminders_enabled: boolean;
  paid: boolean;
  rent_due_date: number | null;
  /** bookings.reminder_stage — the last stage actually sent. */
  reminder_stage: 0 | ReminderStage;
  reminder_sent_at: string | null;
  /** Freshly computed from days overdue (computeCurrentStage). */
  current_stage: 0 | ReminderStage;
  now?: Date;
}): ReminderScheduleLines {
  if (!input.reminders_enabled || input.paid) {
    return { sentLine: null, nextLine: null, nextMissed: false };
  }

  const sentLine =
    input.reminder_stage > 0 && input.reminder_sent_at
      ? `${reminderSentPhrase(input.reminder_stage as ReminderStage)} · ${formatReminderDateTime(input.reminder_sent_at)}`
      : null;

  const next = nextReminderStep({
    rentDueDay: input.rent_due_date,
    remindersEnabled: input.reminders_enabled,
    paidCurrentPeriod: input.paid,
    currentStage: input.current_stage,
    lastSentStage: input.reminder_stage,
    reminderSentAt: input.reminder_sent_at,
    now: input.now,
  });
  const nextLine = next
    ? next.missed
      ? // A scheduled automated send that did not happen. Say the day it was
        // due — the admin needs to see the gap, not a rolling promise.
        `${reminderStagePhrase(next.stage)} due ${formatReminderDateTime(next.at)} · not sent yet`
      : `${reminderNextPhrase(next.stage)} · ${formatReminderDateTime(next.at)}`
    : null;

  return { sentLine, nextLine, nextMissed: next?.missed ?? false };
}
